import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const ValidationWorkflowPanel = ({ pendingApprovals, onApprove, onReject, onRequestDocuments }) => {
  const { t, isRTL } = useLanguage();

  return (
    <div className="bg-card rounded-lg border border-border p-3 sm:p-4 md:p-6 shadow-warm">
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="CheckCircle" size={20} className="text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-foreground truncate">{t('visas.workflowTitle')}</h3>
          <p className="caption text-muted-foreground truncate">
            {pendingApprovals?.length} {pendingApprovals?.length !== 1 ? t('visas.approvalsPlural') : t('visas.approvalsSingular')} {t('visas.pendingApproval')}
          </p>
        </div>
      </div>
      <div className="space-y-4">
        {pendingApprovals?.map((approval) => (
          <div key={approval?.id} className="p-3 sm:p-4 bg-muted/30 rounded-lg border border-border">
            <div className="flex flex-col gap-4 mb-4">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground mb-1 truncate">{approval?.pilgrimName}</p>
                <p className="caption text-muted-foreground mb-2">{approval?.passportNumber}</p>
                <div className="flex flex-wrap gap-2">
                  {approval?.missingDocuments?.map((doc, index) => (
                    <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning text-xs rounded-md border border-warning/20">
                      <Icon name="AlertTriangle" size={12} />
                      <span className="truncate max-w-[150px]">{doc}</span>
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="success"
                  size="sm"
                  iconName="Check"
                  iconPosition="left"
                  onClick={() => onApprove(approval?.id)}
                  className="flex-1"
                >
                  {t('visas.approve')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  iconName="X"
                  iconPosition="left"
                  onClick={() => onReject(approval?.id)}
                  className="flex-1"
                >
                  {t('visas.reject')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="FileText"
                  iconPosition="left"
                  onClick={() => onRequestDocuments(approval?.id)}
                  className="flex-1"
                >
                  <span className="hidden sm:inline">{t('visas.requestDocs')}</span>
                  <span className="sm:hidden">{t('visas.requestDocsShort')}</span>
                </Button>
              </div>
            </div>

            <div className="pt-3 border-t border-border">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                <Icon name="Clock" size={14} className="flex-shrink-0" />
                <span className="truncate">{t('visas.submittedOn')} {new Date(approval.submittedDate)?.toLocaleDateString('en-US')}</span>
              </div>
            </div>
          </div>
        ))}

        {pendingApprovals?.length === 0 && (
          <div className="text-center py-6 sm:py-8">
            <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
            <p className="text-muted-foreground text-sm sm:text-base">{t('visas.noApprovalsWaiting')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidationWorkflowPanel;